import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { clsx } from 'clsx';
import { useLanguage } from '../../../features/language/useLanguage';
import { UnitToggle } from '../../common/UnitToggle';
import { WeightInput } from '../../common/WeightInput';
import { formatRupiah, parseRupiah } from '../../../utils/format';
import type { InventoryModalFormData } from './types';

interface Props {
  unit: 'kg' | 'gram';
  toggleUnit: () => void;
  isEditMode: boolean;
  isPcs: boolean;
  isFinishedGood: boolean;
  showActiveItem: boolean;
  readOnly: boolean;
}

export const StockCostSection: React.FC<Props> = ({
  unit,
  toggleUnit,
  isEditMode,
  isPcs,
  isFinishedGood,
  showActiveItem,
  readOnly,
}) => {
  const { t } = useLanguage();
  const { control, setValue, formState: { errors } } = useFormContext<InventoryModalFormData>();
  const standardCost = useWatch({ control, name: 'standardCost' });

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('inventory.modal.sections.stockCost')}</h2>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <div className="flex justify-between items-center mb-1 h-7">
            <label className="block text-sm font-medium text-gray-700">
              {showActiveItem || isPcs ? t('inventory.modal.fields.quantity') : t('inventory.modal.fields.initialQuantity')}
            </label>
            {!isPcs && <UnitToggle unit={unit} onToggle={toggleUnit} disabled={readOnly} />}
          </div>
          {isPcs ? (
            <Controller
              control={control}
              name="quantity"
              render={({ field: { value, onChange } }) => (
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={isEditMode || readOnly}
                  value={typeof value === 'number' ? value : 0}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const digitsOnly = raw.replace(/[^\d]/g, '');
                    onChange(digitsOnly === '' ? 0 : Number(digitsOnly));
                  }}
                  onKeyDown={(e) => {
                    if (e.ctrlKey || e.metaKey || e.altKey) return;
                    const key = e.key;
                    const isNavigation =
                      key === 'Backspace' ||
                      key === 'Delete' ||
                      key === 'Tab' ||
                      key === 'Escape' ||
                      key === 'Enter' ||
                      key === 'Home' ||
                      key === 'End' ||
                      key === 'ArrowLeft' ||
                      key === 'ArrowRight' ||
                      key === 'ArrowUp' ||
                      key === 'ArrowDown';
                    if (isNavigation) return;
                    if (key.length === 1 && !/^\d$/.test(key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    if (/^\d*$/.test(text)) return;
                    e.preventDefault();
                    const digitsOnly = text.replace(/[^\d]/g, '');
                    if (digitsOnly === '') return;
                    const el = e.currentTarget;
                    const start = el.selectionStart ?? el.value.length;
                    const end = el.selectionEnd ?? el.value.length;
                    const nextRaw = el.value.slice(0, start) + digitsOnly + el.value.slice(end);
                    const nextDigits = nextRaw.replace(/[^\d]/g, '');
                    onChange(nextDigits === '' ? 0 : Number(nextDigits));
                  }}
                  className={clsx(
                    'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2',
                    errors.quantity ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
                    (isEditMode || readOnly) && 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  )}
                  data-test-id="inventory-create-quantity"
                />
              )}
            />
          ) : (
            <Controller
              control={control}
              name="quantity"
              render={({ field: { value, onChange } }) => (
                <WeightInput
                  value={typeof value === 'number' ? value : 0}
                  onChange={(valKg) => onChange(valKg ?? 0)}
                  unit={unit}
                  disabled={isEditMode || readOnly}
                  className={clsx(
                    'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2',
                    errors.quantity ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
                    (isEditMode || readOnly) && 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  )}
                  data-test-id="inventory-create-quantity"
                />
              )}
            />
          )}
          {errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity.message}</p>}
        </div>

        <div>
          <div className="flex justify-between items-center mb-1 h-7">
            <label className="block text-sm font-medium text-gray-700">
              {t('inventory.modal.fields.standardCost')}
            </label>
            {!isPcs && <div className="invisible"><UnitToggle unit={unit} onToggle={() => {}} /></div>}
          </div>

          <input
            type="text"
            value={formatRupiah(standardCost)}
            onChange={(e) => {
              const raw = parseRupiah(e.target.value);
              setValue('standardCost', raw, { shouldValidate: true, shouldDirty: true });
            }}
            className={clsx(
              'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
              errors.standardCost ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            )}
            data-test-id="inventory-create-cost"
            disabled={readOnly}
          />
          {errors.standardCost && <p className="mt-1 text-xs text-red-600">{errors.standardCost.message}</p>}
        </div>

        {!isFinishedGood && (
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.reorderWarning')}<span className="text-red-500">*</span></label>
              <UnitToggle unit={unit} onToggle={toggleUnit} disabled={readOnly} />
            </div>
            <Controller
              control={control}
              name="reorderPoint"
              render={({ field: { value, onChange } }) => (
                <WeightInput
                  value={typeof value === 'number' ? value : 0}
                  onChange={(valKg) => onChange(valKg ?? 0)}
                  unit={unit}
                  disabled={readOnly}
                  className={clsx(
                    'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
                    errors.reorderPoint ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  )}
                  data-test-id="inventory-create-reorder-point"
                />
              )}
            />
            {errors.reorderPoint && <p className="mt-1 text-xs text-red-600">{errors.reorderPoint.message}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
